'use client';

import * as React from 'react';

/**
 * Trap focus inside a container element while `isActive` is true.
 *
 * Behavior:
 * - On activation, moves focus to the first focusable element inside the
 *   container (if focus is not already inside).
 * - While active, Tab / Shift+Tab cycle within the container's focusable
 *   elements; focus cannot escape via keyboard.
 * - On deactivation, restores focus to whatever element was focused at the
 *   moment of activation (the "return focus" pattern WCAG calls for).
 *
 * This is the minimal A11y-correct focus trap for overlays that already
 * carry `role="dialog"` + `aria-modal="true"`. `SlidePanel` and `ModalShell`
 * (and therefore `BaseModal`) both use it, so the two stay consistent.
 *
 * Only the innermost active trap handles Tab — see `activeTraps` below. Two
 * traps active at once is reachable today (the photo-details `SlidePanel` stays
 * open under the delete `ConfirmDialog`), and without that check they deadlock:
 * both listen on `document`, the outer one sees focus outside itself and pulls
 * it back, the inner one pulls it back again, and Tab never advances.
 *
 * Callers must pass an `isActive` that is only true once the container is
 * actually in the DOM. For an overlay that mounts conditionally, `isOpen`
 * alone is one render too early: the activation effect would find a null
 * ref and skip initial focus, while Tab containment (which reads the ref
 * lazily) would still appear to work.
 */
/**
 * Active traps, innermost last. Module-level because stacked overlays are
 * separate component subtrees with no shared ancestor to hold this.
 */
const activeTraps: Array<React.RefObject<HTMLElement | null>> = [];

export function useFocusTrap<T extends HTMLElement>(isActive: boolean): React.RefObject<T | null> {
  const containerRef = React.useRef<T | null>(null);
  const returnFocusRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    if (!isActive) return;

    const trapEntry = containerRef as React.RefObject<HTMLElement | null>;
    activeTraps.push(trapEntry);

    // Remember whatever was focused when the trap activated so we can
    // restore it on close.
    const previouslyFocused = document.activeElement as HTMLElement | null;
    returnFocusRef.current = previouslyFocused;

    const container = containerRef.current;
    if (container) {
      // Move focus inside the container on open if it isn't already.
      const alreadyInside = previouslyFocused != null && container.contains(previouslyFocused);
      if (!alreadyInside) {
        const focusables = getFocusable(container);
        const target = focusables[0] ?? container;
        // Make the container itself programmatically focusable as a last
        // resort, so screen readers land somewhere coherent.
        if (target === container && !container.hasAttribute('tabindex')) {
          container.setAttribute('tabindex', '-1');
        }
        target.focus();
      }
    }

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      // Defer to the innermost trap; a backgrounded one must not fight it.
      if (activeTraps[activeTraps.length - 1] !== trapEntry) return;
      const el = containerRef.current;
      if (!el) return;

      const focusables = getFocusable(el);
      if (focusables.length === 0) {
        // Nothing to contain, so let the browser move focus normally.
        // Calling preventDefault() here would be a hard keyboard trap (WCAG
        // SC 2.1.2, No Keyboard Trap) with no exit at all: `ConfirmDialog`
        // reaches zero focusables on every loading run — both footer buttons
        // disabled, no close button — and gates Escape and backdrop-click on
        // the same `isLoading`, so Tab is the only way out and swallowing it
        // would freeze the keyboard until the request resolved.
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;

      if (e.shiftKey) {
        if (document.activeElement === first || !el.contains(document.activeElement)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last || !el.contains(document.activeElement)) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => {
      document.removeEventListener('keydown', handleTabKey);
      const stackIndex = activeTraps.indexOf(trapEntry);
      if (stackIndex !== -1) activeTraps.splice(stackIndex, 1);
      // Restore focus to the element that had it before the trap opened.
      const toRestore = returnFocusRef.current;
      returnFocusRef.current = null;
      if (toRestore && typeof toRestore.focus === 'function') {
        // Guard: the element may have been removed from the DOM.
        if (document.contains(toRestore)) {
          toRestore.focus();
        }
      }
    };
  }, [isActive]);

  return containerRef;
}

function getFocusable(root: HTMLElement): HTMLElement[] {
  const nodes = root.querySelectorAll<HTMLElement>(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  const candidates = Array.from(nodes).filter((el) => {
    if (el.hasAttribute('disabled')) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;
    return true;
  });

  // Exclude focusables that aren't rendered — e.g. an `md:hidden` mobile-only
  // bar on desktop. A `display:none` element has zero client rects; an on-screen
  // one has at least one. `.focus()` on an unrendered element is a silent no-op,
  // so treating one as the first or last focusable breaks the trap outright.
  // Fall back to the full list where layout can't be measured — jsdom computes
  // no rects for anything, so this fallback is what every test here runs on.
  const rendered = candidates.filter((el) => el.getClientRects().length > 0);
  return rendered.length > 0 ? rendered : candidates;
}
